import Glider from "glider-js"
import { useEffect, useMemo, useRef, useState } from "react"
import cn from "classnames"
import { useMedia } from "react-use"
import { useAtom } from "@reatom/npm-react"

import { Icon, ProfileCard } from "components"
import { promotedProfilesAtom } from "modules/PsyCatalog/common/models/searchRequest"
import { getLocationPath } from "utils/helpers"
import { response } from "modules/PsyCatalog/common/PromotedPsys/constants"
import { APP_STATUSES } from "utils/AppStatusContext"

export function PromotedPsys() {
    const [psys] = useAtom(promotedProfilesAtom)
    const showSlider = psys.length > 0
    const listRef = useRef<HTMLDivElement | null>(null)
    const gliderRef = useRef<Glider | null>(null)
    const arrowLeftRef = useRef<HTMLDivElement>(null)
    const arrowRightRef = useRef<HTMLDivElement>(null)
    const [sliderPosition, setSliderPosition] = useState(0)
    const isTablet = useMedia("(max-width: 953px)")
    const isMobile = useMedia("(max-width: 640px)")
    const pathName = getLocationPath()

    const showArrows = useMemo(() => {
        if (!showSlider) return
        if (isMobile) {
            return psys.length > 1
        }
        if (isTablet) {
            return psys.length > 2
        }
        return psys.length > 4
    }, [showSlider, isTablet, isMobile])

    const onSlideVisible = (
        value: Glider.GliderEvent<Glider.GliderEventMap["glider-slide-visible"]>
    ) => {
        setSliderPosition(value.detail.slide)
    }

    useEffect(() => {
        if (!showSlider) return
        if (listRef.current) {
            gliderRef.current = new Glider(listRef.current, {
                arrows: {
                    prev: arrowLeftRef.current,
                    next: arrowRightRef.current,
                },
                ...response,
            })

            listRef.current?.addEventListener("glider-slide-visible", onSlideVisible)
        }
        return () => {
            if (listRef.current) {
                listRef.current?.removeEventListener(
                    "glider-slide-visible",
                    onSlideVisible as unknown as (ev: Event) => unknown
                )
            }
            if (gliderRef.current) {
                gliderRef.current?.destroy()
            }
        }
    }, [showSlider])

    useEffect(() => {
        if (!showSlider) return
        setSliderPosition(0)
    }, [isTablet, isMobile, showSlider])

    if (!showSlider) return null

    return (
        <div className="promoted-psys">
            <div className="promoted-psys__container b-container">
                <div className="promoted-psys__head">
                    <div className="blm-h3 promoted-psys__title">Вам могут понравиться</div>
                    <div
                        className={cn("promoted-psys__arrows", {
                            "--show": showArrows,
                        })}
                    >
                        <div
                            className={cn("promoted-psys__arrow", {
                                "is-active ": sliderPosition > 0,
                            })}
                            ref={arrowLeftRef}
                        >
                            <Icon type="left-short-arrow" size={24} />
                        </div>
                        <div
                            className={cn("promoted-psys__arrow", {
                                "is-active ": sliderPosition < psys.length - 1,
                            })}
                            ref={arrowRightRef}
                        >
                            <Icon type="right-short-arrow" size={24} />
                        </div>
                    </div>
                </div>
                <div className="promoted-psys__content">
                    <div className="promoted-psys__list" ref={listRef}>
                        {psys.map((item, index) => {
                            return (
                                <ProfileCard
                                    psy={item}
                                    key={index}
                                    index={index.toString()}
                                    className="promoted-psys__item"
                                    from={APP_STATUSES.search_page}
                                    fromLink={pathName}
                                    header={{
                                        classes: {
                                            header: "promoted-psys__header",
                                            mainInfo: "promoted-psys__info",
                                            specialization: "promoted-psys__specialization",
                                            title: "promoted-psys__name",
                                            additionalInfo: "promoted-psys__additional-info mgb-4",
                                        },
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}
